import { applyFilter } from "./filters";
import { colorScheme } from "./graph-utils";
import {
  columnGroups,
  type DisplayColumnNames,
  displayColumns,
} from "./shared";
import { type FilterCondition, type Row } from "./types";

export type ExtendedRow = Row & {
  interpoloated_metric: number | null;
  fullDescription: string;
  legendString: string;
} & {
  [key in DisplayColumnNames]: string | null;
};

interface Result {
  filteredRows: ExtendedRow[];
  discriminativeColumns: Array<(typeof displayColumns)[number]>;
  colorScale: { domain: string[]; range: string[] };
}

type MetricColumn = Exclude<
  (typeof columnGroups)["metrics"][number],
  "queries_per_second"
>;

interface Curve {
  xs: number[];
  ys: number[];
  rows: ExtendedRow[];
}

interface ActiveSegment {
  curveIndex: number;
  y: number; // Current y coordinate at sweep line
  slope: number; // Current segment slope
  endX: number; // X coordinate where this segment ends
  curve: Curve; // Reference to full curve for transitions
  segmentIndex: number; // Index of current segment in curve
}

// Find intersection point between two line segments given their current state
function findNextIntersection(
  s1: ActiveSegment,
  s2: ActiveSegment,
  currentX: number,
): number | null {
  // If segments end before they could intersect
  const endX = Math.min(s1.endX, s2.endX);
  if (endX <= currentX) return null;

  // If lines are parallel
  if (Math.abs(s1.slope - s2.slope) < 1e-10) return null;

  // Find intersection point
  // y1 = m1(x - x0) + y0
  // y2 = m2(x - x0) + y0
  // m1(x - x0) + y0 = m2(x - x0) + y0
  // x = x0 + (y2 - y1)/(m1 - m2)
  const x = currentX + (s2.y - s1.y) / (s1.slope - s2.slope);

  // Check if intersection occurs before either segment ends
  if (x > currentX && x <= endX) {
    return x;
  }

  return null;
}

// Calculate fraction of x-axis where each curve is lowest
function calculateLowestFractions(curves: Curve[]): Map<ExtendedRow[], number> {
  const fractions = new Map<ExtendedRow[], number>();
  if (curves.length === 0) return fractions;

  // Initialize fractions
  curves.forEach((curve) => fractions.set(curve.rows, 0));

  // Create events for segment endpoints
  type Event = {
    x: number;
    type: "start" | "end";
    curveIndex: number;
  };

  // Create initial events from all segment endpoints
  const events: Event[] = [];
  curves.forEach((curve, curveIndex) => {
    curve.xs.forEach((x) => {
      events.push({ x, type: "start", curveIndex });
      events.push({ x, type: "end", curveIndex });
    });
  });

  // Sort events by x coordinate
  events.sort((a, b) => {
    if (a.x !== b.x) return a.x - b.x;
    // For same x, process ends before starts
    return a.type === "end" ? -1 : 1;
  });

  // Active segments sorted by y-coordinate
  const activeSegments: ActiveSegment[] = [];

  let prevX = events[0].x;
  let lowestCurveIndex: number | null = null;

  // Process events from left to right
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    const x = event.x;
    // Only access curve for non-intersection events
    const curve = event.curveIndex >= 0 ? curves[event.curveIndex] : undefined;

    // Add fraction for previous interval
    if (prevX !== undefined && lowestCurveIndex !== null) {
      const totalWidth = events[events.length - 1].x - events[0].x;
      const fraction = (x - prevX) / totalWidth;
      if (lowestCurveIndex >= 0) {
        const lowestCurve = curves[lowestCurveIndex];
        const currentFraction = fractions.get(lowestCurve.rows) || 0;
        fractions.set(lowestCurve.rows, currentFraction + fraction);
      }
    }

    // Update active segments to current x position
    for (const segment of activeSegments) {
      segment.y += segment.slope * (x - prevX);
    }

    if (event.type === "end") {
      // Remove segment that ends
      const index = activeSegments.findIndex(
        (s) => s.curveIndex === event.curveIndex,
      );
      if (index !== -1) {
        activeSegments.splice(index, 1);
      }
    }

    if (event.type === "start") {
      // Only process curve segments for non-intersection events
      if (event.curveIndex >= 0) {
        if (!curve) {
          throw new Error("Expected curve for non-intersection start event");
        }
        // Find current segment index in curve
        const segmentIndex = curve.xs.findIndex(
          (x2, idx) =>
            idx < curve.xs.length - 1 && x >= x2 && x < curve.xs[idx + 1],
        );

        if (segmentIndex >= 0) {
          // Create new active segment
          const x1 = curve.xs[segmentIndex];
          const x2 = curve.xs[segmentIndex + 1];
          const y1 = curve.ys[segmentIndex];
          const y2 = curve.ys[segmentIndex + 1];
          const slope = (y2 - y1) / (x2 - x1);

          const newSegment: ActiveSegment = {
            curveIndex: event.curveIndex,
            y: y1 + slope * (x - x1),
            slope,
            endX: x2,
            curve,
            segmentIndex,
          };

          // Insert into activeSegments maintaining y-coordinate order
          const insertIndex = activeSegments.findIndex(
            (s) => s.y > newSegment.y,
          );
          if (insertIndex === -1) {
            activeSegments.push(newSegment);
          } else {
            activeSegments.splice(insertIndex, 0, newSegment);
          }
        }
      }
    }

    // Lowest curve is first active segment (they're sorted by y)
    lowestCurveIndex =
      activeSegments.length > 0 ? activeSegments[0].curveIndex : null;

    // Find next intersection point by checking consecutive pairs
    let nextIntersectionX = Infinity;
    if (activeSegments.length > 1) {
      for (let j = 0; j < activeSegments.length - 1; j++) {
        const intersection = findNextIntersection(
          activeSegments[j],
          activeSegments[j + 1],
          x,
        );
        if (intersection !== null && intersection < nextIntersectionX) {
          nextIntersectionX = intersection;
        }
      }
    }

    // If we found an intersection before the next event, insert it
    if (nextIntersectionX < Infinity && nextIntersectionX < events[i + 1]?.x) {
      events.splice(
        i + 1,
        0,
        { x: nextIntersectionX, type: "end", curveIndex: -1 },
        { x: nextIntersectionX, type: "start", curveIndex: -1 },
      );
    }

    prevX = x;
  }

  return fractions;
}

export function processRows(
  rows: Row[],
  filterConditions: FilterCondition[],
  metricColumn?: MetricColumn,
): Result {
  const filteredRows = rows
    .filter((row) => {
      return filterConditions.every((condition) => applyFilter(row, condition));
    })
    .map((row) => {
      return {
        ...row,
        interpoloated_metric: null,
        ...(Object.fromEntries(
          displayColumns.map((c) => [c.label, c.value(row)]),
        ) as Record<DisplayColumnNames, string | null>),
      } as ExtendedRow;
    });

  // Group rows by non-tooltip display columns
  // const nonTooltipColumns = displayColumns.filter((c) => !("onlyTooltip" in c));
  // const groupedByNonTooltip = Object.groupBy(filteredRows, (r) =>
  //   JSON.stringify(nonTooltipColumns.map((c) => r[c.label])),
  // );

  // For each non-tooltip group, calculate which curves are lowest where
  // for (const rows of Object.values(groupedByNonTooltip)) {
  //   if (!rows) continue;

  // Group by all display columns to get individual curves
  const groupedRows = Object.groupBy(filteredRows, (r) =>
    JSON.stringify(displayColumns.map((c) => r[c.label])),
  );

  // Convert grouped rows to curves with sorted points
  if (metricColumn) {
    const curves: Curve[] = Object.values(groupedRows)
      .filter((curveRows): curveRows is ExtendedRow[] => curveRows !== null)
      .map((curveRows) => {
        const sortedRows = curveRows.toSorted(
          (a, b) => a.queries_per_second - b.queries_per_second,
        );
        return {
          xs: sortedRows.map((r) => r.queries_per_second),
          ys: sortedRows.map((r) => r[metricColumn]),
          rows: curveRows,
        };
      });

    // Calculate fractions where each curve is lowest
    const fractions = calculateLowestFractions(curves);

    // Set interpolated_metric to negative fraction for each row
    for (const [rows, fraction] of fractions) {
      for (const row of rows) {
        row.interpoloated_metric = -fraction;
      }
    }
  }

  const discriminativeColumns = displayColumns
    .map((c) => {
      const values = new Set(filteredRows.map((r) => r[c.label]));
      return {
        column: c,
        values,
      };
    })
    .filter((c) => c.values.size > 1)
    .map((c) => c.column);

  const resultRows = filteredRows.map((r) => ({
    ...r,
    fullDescription:
      discriminativeColumns.map((c) => r[c.label]).join(", ") || "–",
    legendString:
      // discriminativeColumns
      //   .filter((c) => !("onlyTooltip" in c))
      //   .map((c) => r[c.label])
      //   .join(", ") ||
      // Fallback if all the discriminative columns are the same
      displayColumns
        .filter((c) => ["Framework", "GPU"].includes(c.label))
        .map((c) => r[c.label])
        .join(", "),
  }));

  const legends = Array.from(new Set(resultRows.map((r) => r.legendString)));
  function getColorIdx(legend: string) {
    if (legend.toLowerCase().includes("sglang")) return 13;
    if (legend.toLowerCase().includes("vllm")) return 10;
    if (legend.toLowerCase().includes("tensorrt-llm")) return 1;
    return 0;
  }
  const colorScale = {
    domain: legends,
    range: legends.map((l) => colorScheme[getColorIdx(l)]),
  };

  return {
    filteredRows: resultRows,
    discriminativeColumns,
    colorScale,
  };
}
